import { TZ } from '@phensley/timezone';
import { timePeriod } from './interval';
import { adjustTime, adjustYM, clampYMD, jdFromUnixEpoch, timeToMillis, toYMD, truncateFields, unixEpochFromJD, } from './math';
/**
 * Add years, days, hours, etc, to dates. This function always returns
 * the result in UTC.
 *
 * Compatible with the Temporal API (except for rounding; we only truncate)
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Temporal/ZonedDateTime/add
 */
export var add = function (orig, added) {
    var _a;
    // Check if only date or time fields are present.
    var hasTime = added.hour || added.minute || added.second || added.millis;
    var hasDate = added.year || added.month || added.week || added.day;
    // Procedure:
    // 1. Add YEARS and MONTHS to base date.
    // 2. Clamp the original day to ensure the new date is valid, e.g.
    //    "Jan 31, 2024" + 1 month gives "Feb 29, 2024" instead of "Feb 31, 2024"
    //    or "Mar 2, 2024".
    // 3. Add the WEEKS and DAYS to the new date.
    // 4. Combine the new date with the original time-of-day. This finishes adding
    //    all of the date fields.
    // 3. Calculate the UNIX epoch timestamp for the new date.
    // 4. Disambiguate the epoch wall time to select the correct timezone offset.
    // 5. Finally add the time to the new epoch.
    // Get Julian day and milliseconds in UTC
    var jd = orig.modifiedJulianDay();
    var millis = orig.millisecondsInDay();
    var offset = orig.timeZoneOffset();
    // If nothing is being added, return the original date.
    if (!hasDate && !hasTime) {
        return [jd, millis - offset];
    }
    // Add integers only
    var truncated = truncateFields(added);
    // If only time is being added, skip the year/month logic
    // and add directly to the timestamp.
    if (!hasDate) {
        var _b = adjustTime(millis - offset, truncated), timeDays = _b[0], ms = _b[1];
        jd += timeDays;
        return [jd, ms];
    }
    // Work in UTC then translate back later
    var date = orig.set({ zoneId: 'UTC' });
    var internal = date;
    // Add years and months directly to the date fields.
    var _c = toYMD(date), year = _c.year, month = _c.month, day = _c.day;
    // Break milliseconds down into days, hours, minutes, etc.
    var time = millisToTimePeriod(millis);
    // Add date fields
    (_a = dateAdd({ year: year, month: month, day: day }, truncated), year = _a.year, month = _a.month, day = _a.day);
    // Compute the Julian day of the year, month, and add days.
    jd = internal.monthStart(year, month - 1, false) + day + time.day;
    // Convert Julian day and milliseconds to Unix epoch to find timezone offset.
    var epoch = unixEpochFromJD(jd, millis);
    // Get the wall clock timezone offset. We use the CalendarDate's timezoneId
    // knowing it will always resolve to a valid timezone record, since constructing
    // an instance must pass through the timezone resolution process.
    var wall = TZ.fromWall(orig.timeZoneId(), epoch);
    epoch = wall[0];
    // Add the time components.
    epoch += timeToMillis(truncated);
    // Convert the UNIX epoch back to Julian day and milliseconds-in-day.
    return jdFromUnixEpoch(epoch);
};
/**
 * Add the date parts of a TimePeriod to a date.
 */
export var dateAdd = function (date, added) {
    var _a, _b;
    var year = date.year, month = date.month, day = date.day;
    year += added.year;
    month += added.month;
    // Add excess months to years
    (_a = adjustYM(year, month), year = _a.year, month = _a.month);
    // Ensure the day falls into a valid range for the new year and month.
    (_b = clampYMD(year, month, day), year = _b.year, month = _b.month, day = _b.day);
    // Add days from all sources
    day += added.week * 7 + added.day;
    return { year: year, month: month, day: day };
};
export var millisToTimePeriod = function (millis) {
    var r = timePeriod();
    r.millis = millis;
    r.hour = (r.millis / 3600000 /* CalendarConstants.ONE_HOUR_MS */) | 0;
    r.millis -= r.hour * 3600000 /* CalendarConstants.ONE_HOUR_MS */;
    r.minute = (r.millis / 60000 /* CalendarConstants.ONE_MINUTE_MS */) | 0;
    r.millis -= r.minute * 60000 /* CalendarConstants.ONE_MINUTE_MS */;
    r.second = (r.millis / 1000 /* CalendarConstants.ONE_SECOND_MS */) | 0;
    r.millis -= r.second * 1000 /* CalendarConstants.ONE_SECOND_MS */;
    return r;
};
//# sourceMappingURL=add.js.map